import {
  Activity,
  Box,
  ChevronRight,
  Clock,
  Download,
  Eye,
  EyeOff,
  FileText,
  Filter,
  GripVertical,
  Hash,
  Lock,
  Mail,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Upload,
  User
} from 'lucide-react';
import { useToast } from '../../../../hooks/useToast';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import React, { useEffect, useState, useRef } from 'react';
import { FORM_USER_STATUSES, USER_STATUSES_FILTER } from '../../../../constants/users';
import { useApiRoles } from '../../../../hooks/useApiRoles';
import { useApiUsers } from '../../../../hooks/useApiUsers';
import { MOCK_USERS } from '../../../../mocks/Users.mocks';
import { UserData } from '../../../../types/User.types';
import { DataTable } from '../../../../components/shared/DataTable/DataTable';
import { FilterSelect } from '../../../../components/shared/FilterSelect/FilterSelect';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { TableSearch } from '../../../../components/shared/TableSearch/TableSearch';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, editAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import Modal from '../../../../components/shared/Modal/Modal';
import './UsersView.css';

export const UsersView = ({
  onMenuOpen = () => {
  },
  menuCloseSignal = 0,
  selectedUser: externalSelectedUser,
  setSelectedUser: externalSetSelectedUser,
  isFormOpen: externalIsFormOpen,
  setIsFormOpen: externalSetIsFormOpen
}: {
  onMenuOpen?: () => void,
  menuCloseSignal?: number,
  selectedUser?: UserData | null,
  setSelectedUser?: (user: UserData | null) => void,
  isFormOpen?: boolean,
  setIsFormOpen?: (open: boolean) => void
} = {}) => {
  const { notify } = useToast();
  const [internalSelectedUser, setInternalSelectedUser] = useState<UserData | null>(null);
  const [internalIsFormOpen, setInternalIsFormOpen] = useState(false);
  const [deleteModalUser, setDeleteModalUser] = useState<UserData | null>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const selectedUser = externalSelectedUser !== undefined ? externalSelectedUser : internalSelectedUser;
  const setSelectedUser = externalSetSelectedUser !== undefined ? externalSetSelectedUser : setInternalSelectedUser;

  const isFormOpen = externalIsFormOpen !== undefined ? externalIsFormOpen : internalIsFormOpen;
  const setIsFormOpen = externalSetIsFormOpen !== undefined ? externalSetIsFormOpen : setInternalIsFormOpen;
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isRoleFilterOpen, setIsRoleFilterOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [formStatus, setFormStatus] = useState<string>('active');
  const [formFirstName, setFormFirstName] = useState<string>('');
  const [formLastName, setFormLastName] = useState<string>('');
  const [formEmail, setFormEmail] = useState<string>('');
  // '' = sin supervisor propio (en BD queda NULL y aprueba el supervisor por defecto).
  const [formSupervisorId, setFormSupervisorId] = useState<string>('');
  const [formIsDefaultSupervisor, setFormIsDefaultSupervisor] = useState<boolean>(false);

  const initialFormStateRef = useRef({
    status: 'active',
    firstName: '',
    lastName: '',
    email: '',
    supervisorId: '',
    isDefaultSupervisor: false
  });

  // Catálogo completo (no paginado) para el desplegable de supervisor.
  const { users: supervisorCandidates, reload: reloadSupervisorCandidates } = useApiUsers();
  const defaultSupervisor = supervisorCandidates.find(u => u.isDefaultSupervisor) || null;

  const { roles: apiRoles, loading: rolesLoading, error: rolesError } = useApiRoles();
  const rolesUnavailable = !rolesLoading && (rolesError || apiRoles.length === 0);
  const roleFilterOptions = apiRoles.map(r => ({ value: r.id, label: r.name.toUpperCase(), icon: Lock }));

  useEffect(() => {
    if (selectedUser) {
      const initialData = {
        status: selectedUser.status || 'active',
        firstName: selectedUser.firstName || '',
        lastName: selectedUser.lastName || '',
        email: selectedUser.email || '',
        supervisorId: selectedUser.supervisorId || '',
        isDefaultSupervisor: selectedUser.isDefaultSupervisor === true
      };
      initialFormStateRef.current = initialData;
      setFormStatus(initialData.status);
      setFormFirstName(initialData.firstName);
      setFormLastName(initialData.lastName);
      setFormEmail(initialData.email);
      setFormSupervisorId(initialData.supervisorId);
      setFormIsDefaultSupervisor(initialData.isDefaultSupervisor);
    } else {
      const initialData = {
        status: 'active',
        firstName: '',
        lastName: '',
        email: '',
        supervisorId: '',
        isDefaultSupervisor: false
      };
      initialFormStateRef.current = initialData;
      setFormStatus('active');
      setFormFirstName('');
      setFormLastName('');
      setFormEmail('');
      setFormSupervisorId('');
      setFormIsDefaultSupervisor(false);
    }
  }, [selectedUser, isFormOpen, apiRoles]);

  const isFormDirty = () => {
    const init = initialFormStateRef.current;
    return (
      formStatus !== init.status ||
      formFirstName !== init.firstName ||
      formLastName !== init.lastName ||
      formEmail !== init.email ||
      formSupervisorId !== init.supervisorId ||
      formIsDefaultSupervisor !== init.isDefaultSupervisor
    );
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [columnOrder, setColumnOrder] = useState<string[]>([
    'id', 'name', 'email', 'role', 'department', 'status', 'lastLogin'
  ]);

  useEffect(() => {
    if (menuCloseSignal > 0) {
      closeLocalMenus();
    }
  }, [menuCloseSignal]);

  // Clic fuera: cierra los desplegables y el panel (con el aviso de cambios sin
  // guardar de siempre). Las zonas con data-keep-detail y los portales no cierran.
  useClickOutside(isDetailKeepZone, () => {
    closeLocalMenus();

    if (selectedUser || isFormOpen) {
      if (isFormDirty()) {
        setShowDiscardModal(true);
      } else {
        setSelectedUser(null);
        setIsFormOpen(false);
      }
    }
  }, true);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    id: true,
    name: true,
    email: true,
    role: true,
    status: true,
    lastLogin: true,
    department: true,
  });

  const toggleColumn = (col: string) => {
    const visibleCount = Object.values(visibleColumns).filter(Boolean).length;
    if (visibleColumns[col] && visibleCount <= 4) return;
    setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }));
  };

  const closeLocalMenus = () => {
    setIsConfigOpen(false);
    setIsRoleFilterOpen(false);
    setIsStatusFilterOpen(false);
    setOpenActionMenuId(null);
  };

  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let url = `/list?page=${currentPage}&page_size=50`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${encodeURIComponent(statusFilter)}`;
      if (roleFilter && roleFilter !== 'all') url += `&role_id=${encodeURIComponent(roleFilter)}`;

      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (userCookie && userCookie.token) {
        url += `&access_token=${encodeURIComponent(userCookie.token)}`;
      }

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
      let res = await ObexRequestsHandlerP80.get(url);

      if (res.data) res = res.data;

      const mapUser = (u: any): UserData => ({
        id: String(u.id),
        name: `${u.name} ${u.last_name || ''}`.trim(),
        firstName: u.name || '',
        lastName: u.last_name || '',
        email: u.email,
        role: u.role_name ? u.role_name.toUpperCase() : 'SIN_ROL',
        roleId: u.role_id ? String(u.role_id) : undefined,
        roleName: u.role_name || undefined,
        status: u.status as any,
        lastLogin: u.created_at || new Date().toISOString(),
        department: 'IT',
        groupNames: typeof u.group_names === 'string' && u.group_names.trim() !== ''
          ? u.group_names.split(';').map((s: string) => s.trim()).filter(Boolean)
          : [],
        inheritedRoles: typeof u.role_names === 'string' && u.role_names.trim() !== ''
          ? u.role_names.split(';').map((s: string) => s.trim()).filter(Boolean)
          : [],
        supervisorId: u.supervisor_id !== null && u.supervisor_id !== undefined ? String(u.supervisor_id) : null,
        isDefaultSupervisor: u.is_default_supervisor === true,
      });

      const responsePayload = res?.response ?? res?.result?.response;
      if (responsePayload && Array.isArray(responsePayload.data)) {
        const mappedUsers: UserData[] = responsePayload.data.map(mapUser);
        setUsers(mappedUsers);
        setTotalRecords(responsePayload.total || mappedUsers.length);
      } else if (responsePayload && Array.isArray(responsePayload)) {
        const mappedUsers: UserData[] = responsePayload.map(mapUser);
        setUsers(mappedUsers);
        setTotalRecords(mappedUsers.length);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchQuery, statusFilter, roleFilter, currentPage, refreshKey]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, roleFilter]);

  // The backend already filters by search/status/role_id, so no local filtering needed.
  const filteredUsers = users;


  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchCurrentUserId = async () => {
      try {
        const Cookies = (await import('universal-cookie')).default;
        const cookies = new Cookies();
        const userCookie = cookies.get('101Obex');
        if (userCookie?.user?.id) {
          setCurrentUserId(String(userCookie.user.id));
        } else if (userCookie?.user_id) {
          setCurrentUserId(String(userCookie.user_id));
        } else if (userCookie?.token) {
          const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
          let meRes = await ObexRequestsHandlerP80.get('/me', { access_token: userCookie.token });
          if (meRes.data) meRes = meRes.data;
          const profile = meRes?.response?.user ?? meRes?.result?.response?.user;
          if (profile?.id) {
            setCurrentUserId(String(profile.id));
          }
        }
      } catch (err) {
        console.error("Error fetching current user profile:", err);
      }
    };
    fetchCurrentUserId();
  }, []);

  const isSelf = Boolean(selectedUser && currentUserId && String(selectedUser.id) === String(currentUserId));

  const handleDeleteUser = (userToDelete?: UserData | null) => {
    const target = userToDelete || selectedUser;
    if (!target) return;

    if (currentUserId && String(target.id) === String(currentUserId)) {
      notify('Aviso', 'No puedes eliminar tu propio usuario', 'warning');
      return;
    }

    setDeleteModalUser(target);
  };

  const confirmDeleteUser = async () => {
    if (!deleteModalUser) return;
    const target = deleteModalUser;

    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (!userCookie || !userCookie.token) {
        notify('Error', 'No hay token de acceso', 'warning');
        return;
      }

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
      let res = await ObexRequestsHandlerP80.get('/delete', {
        access_token: userCookie.token,
        user_id: target.id
      });
      if (res.data) res = res.data;

      // Note: res.status/res.data.status are always true here regardless of
      // business outcome — the real signal is the message shape below.
      const delResp = res?.response ?? res?.result?.response ?? res?.result?.result;
      // El endpoint responde {status: 'ok', message: '... exitosamente'}; los errores llegan como texto plano.
      const delMsg = typeof delResp === 'string' ? delResp : delResp?.message;
      const success = typeof delMsg === 'string' && delMsg.toLowerCase().includes('exitosamente');
      if (success) {
        if (selectedUser?.id === target.id) {
          setSelectedUser(null);
        }
        setRefreshKey(prev => prev + 1);
        notify('Éxito', 'Usuario eliminado exitosamente', 'info');
      } else {
        notify('Error', 'No se pudo eliminar el usuario', 'warning');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      notify('Error', 'Error de red al eliminar usuario', 'urgent');
    } finally {
      setDeleteModalUser(null);
    }
  };

  const handleSubmit = async () => {
    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (!userCookie || !userCookie.token) {
        notify('Error', 'No hay token de acceso', 'warning');
        return;
      }
      
      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
      
      if (isFormOpen) {
        // Create user — GET /create (access_token, name, last_name, email, username, status).
        // El backend genera la contraseña y el PIN y se los envía al usuario por correo.
        let res = await ObexRequestsHandlerP80.get('/create', {
          access_token: userCookie.token,
          name: formFirstName,
          last_name: formLastName,
          email: formEmail,
          username: formEmail.split('@')[0],
          status: formStatus,
        });

        if (res.data) res = res.data;

        const createdUser = res?.response ?? res?.result?.response;
        if (createdUser && typeof createdUser === 'object' && createdUser.id) {
          setIsFormOpen(false);
          setRefreshKey(prev => prev + 1);
          notify('Éxito', 'Usuario creado. La contraseña y el PIN se han enviado a su correo.', 'info');
        } else {
          notify('Error', 'No se pudo crear el usuario', 'warning');
        }
      } else if (selectedUser) {
        // Update user — GET /update (access_token, user_id, name, last_name, email, username, status)
        let res = await ObexRequestsHandlerP80.get('/update', {
          access_token: userCookie.token,
          user_id: selectedUser.id,
          name: formFirstName,
          last_name: formLastName,
          email: formEmail,
          status: formStatus,
        });

        if (res.data) res = res.data;

        const updatedUser = res?.response ?? res?.result?.response;
        if (updatedUser && typeof updatedUser === 'object' && updatedUser.id) {
          // /update no acepta supervisor: son endpoints aparte, y solo se
          // llaman si el campo cambió. Un '' no se envía (no hay endpoint
          // para quitar supervisor): el usuario sigue con el que tenía.
          const init = initialFormStateRef.current;
          const supervisorErrors: string[] = [];

          if (formSupervisorId && formSupervisorId !== init.supervisorId) {
            let supRes = await ObexRequestsHandlerP80.get('/users_set_supervisor', {
              access_token: userCookie.token,
              user_id: selectedUser.id,
              supervisor_id: formSupervisorId,
            });
            if (supRes.data) supRes = supRes.data;
            const supPayload = supRes?.response ?? supRes?.result?.response;
            if (!(supPayload && typeof supPayload === 'object' && supPayload.id)) {
              supervisorErrors.push(typeof supPayload === 'string' ? supPayload : 'No se pudo asignar el supervisor');
            }
          }

          if (formIsDefaultSupervisor && !init.isDefaultSupervisor) {
            let defRes = await ObexRequestsHandlerP80.get('/users_set_default_supervisor', {
              access_token: userCookie.token,
              user_id: selectedUser.id,
            });
            if (defRes.data) defRes = defRes.data;
            const defPayload = defRes?.response ?? defRes?.result?.response;
            if (!(defPayload && typeof defPayload === 'object' && defPayload.id)) {
              supervisorErrors.push(typeof defPayload === 'string' ? defPayload : 'No se pudo marcar como supervisor por defecto');
            } else {
              reloadSupervisorCandidates();
            }
          }

          setSelectedUser(null);
          setRefreshKey(prev => prev + 1);
          if (supervisorErrors.length > 0) {
            notify('Aviso', `Usuario actualizado, pero: ${supervisorErrors.join('. ')}`, 'warning');
          } else {
            notify('Éxito', 'Usuario actualizado exitosamente', 'info');
          }
        } else {
          notify('Error', 'No se pudo actualizar el usuario', 'warning');
        }
      }
    } catch (error) {
      console.error(error);
      notify('Error', 'Error de red en la operación', 'urgent');
    }
  };

  return (
    <>
      <MasterDetailLayout isOpen={Boolean(selectedUser || isFormOpen)} drawer>
        <div className="users-view-container">
          <div className="users-view-header-row">
            <ModuleHeader title="User_Management_System" subtitle="Control de acceso, perfiles y auditoría de usuarios." />
            <div className="users-view-header-actions">
              <button className="users-view-action-btn" title="Exportar CSV">
                <Download size={16} />
              </button>
              <button className="users-view-action-btn" title="Importar Datos">
                <Upload size={16} />
              </button>
              <button
                className="users-view-action-btn"
                title="Refrescar"
                onClick={fetchUsers}
                disabled={loading}
              >
                <RefreshCw size={16} className={loading ? 'users-view-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Control Bar / Filters */}
          <div className="users-view-filters-container">
            <div className="users-view-filters-row">
              <TableSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="BUSCAR POR NOMBRE, ID O EMAIL..."
              />

              <div className="users-view-filter-group">
                <FilterSelect
                  isOpen={isRoleFilterOpen}
                  onToggle={(next) => {
                    closeLocalMenus();
                    onMenuOpen();
                    setIsRoleFilterOpen(next);
                  }}
                  onSelect={setRoleFilter}
                  value={roleFilter}
                  options={roleFilterOptions}
                  icon={Filter}
                  allLabel="TODOS_LOS_ROLES"
                  disabled={rolesUnavailable}
                  disabledLabel="ROLES_NO_DISPONIBLES"
                />

                <FilterSelect
                  isOpen={isStatusFilterOpen}
                  onToggle={(next) => {
                    closeLocalMenus();
                    onMenuOpen();
                    setIsStatusFilterOpen(next);
                  }}
                  onSelect={setStatusFilter}
                  value={statusFilter}
                  options={USER_STATUSES_FILTER}
                  icon={Activity}
                  allLabel="TODOS_LOS_ESTADOS"
                />
              </div>

              <div className="users-view-config-group" data-keep-detail>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = !isConfigOpen;
                    closeLocalMenus();
                    onMenuOpen();
                    setIsConfigOpen(next);
                  }}
                  className={`users-view-config-btn ${isConfigOpen ? "active" : ""}`}
                >
                  <SlidersHorizontal size={14} />
                  Config_Tabla

                  {isConfigOpen && (
                    <div
                      className="users-view-config-menu"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="users-view-config-header">
                        <SlidersHorizontal size={10} />
                        Configuración_Columnas
                      </div>
                      <ul className="users-view-config-list">
                        {columnOrder.map((key) => {
                          const visible = visibleColumns[key];
                          const Icon = key === 'id' ? Hash :
                            key === 'name' ? User :
                              key === 'email' ? Mail :
                                key === 'role' ? Lock :
                                  key === 'status' ? Activity :
                                    key === 'lastLogin' ? Clock :
                                      key === 'department' ? Box : FileText;
                          const visibleCount = Object.values(visibleColumns).filter(Boolean).length;
                          const canHide = !visible || visibleCount > 4;

                          return (
                            <li
                              key={key}
                              className="users-view-config-item"
                            >
                              <GripVertical size={12} className="users-view-config-item-icon-grip" />
                              <Icon size={12} className={`users-view-config-item-icon ${visible ? "visible" : "hidden"}`} />
                              <span className="users-view-config-item-text">{key}</span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleColumn(key); }}
                                disabled={!canHide}
                                className="users-view-config-item-toggle"
                              >
                                {visible ? <Eye size={10} className="users-view-config-item-toggle-icon visible" /> : <EyeOff size={10} className="users-view-config-item-toggle-icon hidden" />}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); closeLocalMenus(); onMenuOpen(); setSelectedUser(null); setIsFormOpen(true); }}
                  className="users-view-new-user-btn"
                  data-keep-detail
                >
                  <Plus size={14} />
                  Nuevo_Usuario
                </button>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <DataTable
            data={filteredUsers}
            columns={columnOrder.filter(colId => visibleColumns[colId]).map(colId => {
              if (colId === 'id') return { id: 'id', header: 'ID_SYS', className: 'users-view-table-td-id' };
              if (colId === 'name') return {
                id: 'name',
                header: 'Nombre_Completo',
                className: 'users-view-table-td-name',
                render: (user: UserData) => (
                  <>
                    {user.name}
                    {user.isDefaultSupervisor && (
                      <span className="users-view-table-td-role-badge users-view-default-supervisor-badge" title="Supervisor por defecto de compras">
                        SUP_DEFECTO
                      </span>
                    )}
                  </>
                )
              };
              if (colId === 'email') return { id: 'email', header: 'Email_Corporativo', className: 'users-view-table-td-email' };
              if (colId === 'role') return {
                id: 'role',
                header: 'Rol_Heredado',
                render: (user: UserData) => (
                  <span className="users-view-table-td-role-badge">
                    {user.inheritedRoles && user.inheritedRoles.length > 0 ? user.inheritedRoles.join(', ') : 'SIN_ROL'}
                  </span>
                )
              };
              if (colId === 'department') return {
                id: 'department',
                header: 'Grupo(s)',
                className: 'users-view-table-td-dept',
                render: (user: UserData) => (
                  <span>{user.groupNames && user.groupNames.length > 0 ? user.groupNames.join(', ') : 'Sin grupo'}</span>
                )
              };
              if (colId === 'status') return {
                id: 'status',
                header: 'Estado',
                render: (user: UserData) => (
                  <div className="users-view-table-td-status-wrapper">
                    <div className={`users-view-status-dot ${user.status === 'active' ? "active" : user.status === 'locked' ? "locked" : "inactive"}`} />
                    <span className="users-view-status-text">{user.status}</span>
                  </div>
                )
              };
              if (colId === 'lastLogin') return {
                id: 'lastLogin',
                header: 'Últ_Acceso',
                className: 'users-view-table-td-last-login',
                render: (user: UserData) => new Date(user.lastLogin).toLocaleDateString()
              };
              return { id: colId, header: colId };
            })}
            getRowId={(user) => user.id}
            onRowClick={(user) => { setSelectedUser(user); setIsFormOpen(false); }}
            selectedRowId={selectedUser?.id}
            showPagination={true}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            isLoading={loading}
            totalRecords={totalRecords}
            emptyIcon={<Search size={32} className="users-view-empty-icon" />}
            actionsColumn={(user) => (
              <RowActionsMenu
                onOpenChange={(open) => {
                  if (open) {
                    closeLocalMenus();
                    onMenuOpen();
                  }
                }}
                actions={[
                  editAction(() => {
                    setSelectedUser(user);
                    setIsFormOpen(false);
                  }),
                  deleteAction(
                    () => handleDeleteUser(user),
                    currentUserId && String(user.id) === String(currentUserId)
                      ? { disabled: true, title: 'No puedes eliminar tu propio usuario' }
                      : undefined
                  )
                ]}
              />
            )}
          />
        </div>

        {/* Info Form Section */}
        {(selectedUser || isFormOpen) && (
          <SidebarForm
            drawer
            title={isFormOpen ? 'Nuevo_Registro_Usuario' : 'Expediente_Usuario'}
            onClose={() => {
              if (isFormDirty()) {
                setShowDiscardModal(true);
              } else {
                setSelectedUser(null);
                setIsFormOpen(false);
              }
            }}
            submitLabel={isFormOpen ? 'REGISTRAR_USUARIO' : 'ACTUALIZAR_EXPEDIENTE'}
            onSubmit={handleSubmit}
            showDelete={!isFormOpen}
            deleteDisabled={isSelf}
            deleteDisabledTooltip="No puedes eliminar tu propio usuario"
            onDelete={() => handleDeleteUser(selectedUser)}
            metadata={!isFormOpen && selectedUser ? [
              { label: 'ID_Sistema', value: selectedUser.id },
              { label: 'Último_Acceso', value: new Date(selectedUser.lastLogin).toLocaleString() },
              { label: 'Nivel_Seguridad', value: 'L5_AUTH', highlight: true },
              {
                label: 'Supervisor',
                value: selectedUser.supervisorId
                  ? (supervisorCandidates.find(u => u.id === selectedUser.supervisorId)?.name || `ID ${selectedUser.supervisorId}`)
                  : defaultSupervisor
                    ? `${defaultSupervisor.name} (por defecto)`
                    : 'Ninguno — sin supervisor por defecto',
                highlight: selectedUser.isDefaultSupervisor === true
              }
            ] : undefined}
            metadataTitle="Metadatos_Auditoría"
          >
            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Nombre</label>
                <input
                  type="text"
                  value={formFirstName}
                  onChange={e => setFormFirstName(e.target.value)}
                  className="sidebar-form-input"
                  placeholder="EJ. JUAN"
                />
              </div>

              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Apellidos</label>
                <input
                  type="text"
                  value={formLastName}
                  onChange={e => setFormLastName(e.target.value)}
                  className="sidebar-form-input"
                  placeholder="EJ. PÉREZ"
                />
              </div>
            </div>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Email_Corporativo</label>
              <div className="sidebar-form-input-wrapper">
                <Mail className="sidebar-form-input-icon" size={12} />
                <input
                  type="email"
                  value={formEmail}
                  onChange={e => setFormEmail(e.target.value)}
                  className="sidebar-form-input sidebar-form-input-with-icon"
                  placeholder="USUARIO@MARCIALPONS.ES"
                />
              </div>
            </div>

            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Rol_Heredado</label>
                <input
                  type="text"
                  disabled
                  readOnly
                  value={
                    !selectedUser
                      ? 'Se hereda tras asignar el usuario a un grupo'
                      : (selectedUser.inheritedRoles?.join(', ') || 'Sin rol (sin grupo asignado)')
                  }
                  className="sidebar-form-input"
                  title="El rol se hereda de los grupos a los que pertenece; gestiónalo desde Grupos"
                />
              </div>

              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Estado_Actual</label>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = openActionMenuId === 'form-status' ? null : 'form-status';
                    closeLocalMenus();
                    onMenuOpen();
                    setOpenActionMenuId(next);
                  }}
                  className="sidebar-form-select-btn"
                >
                  <div className="sidebar-form-select-content">
                    {(() => {
                      const currentStatus = FORM_USER_STATUSES.find(s => s.value === formStatus) || FORM_USER_STATUSES[0];
                      const StatusIcon = currentStatus.icon;
                      return (
                        <>
                          <StatusIcon size={12} className="sidebar-form-dropdown-item-icon" />
                          <span className="sidebar-form-select-text">{currentStatus.label}</span>
                        </>
                      );
                    })()}
                  </div>
                  <ChevronRight size={10} className={`sidebar-form-select-arrow ${openActionMenuId === 'form-status' ? "open" : ""}`} />
                </button>

                {openActionMenuId === 'form-status' && (
                  <div className="sidebar-form-dropdown">
                    {FORM_USER_STATUSES.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setFormStatus(opt.value); setOpenActionMenuId(null); }}
                        className="sidebar-form-dropdown-item"
                      >
                        <opt.icon size={12} className="sidebar-form-dropdown-item-icon" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Grupo(s)</label>
              <input
                type="text"
                disabled
                readOnly
                value={
                  !selectedUser
                    ? 'Se asigna tras crear el usuario, desde Grupos'
                    : (selectedUser.groupNames?.join(', ') || 'Sin grupo asignado')
                }
                className="sidebar-form-input"
                title="La pertenencia a grupos se gestiona desde la vista Grupos"
              />
            </div>

            {/* Supervisor: aprueba las órdenes de compra del usuario. Sin uno
                propio (NULL en BD) aprueba el marcado por defecto, que es lo que
                se muestra preseleccionado; elegir a otro lo fija explícitamente. */}
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Supervisor_Compras</label>
              <div className="sidebar-form-input-wrapper">
                <select
                  value={formSupervisorId}
                  disabled={!selectedUser}
                  onChange={e => setFormSupervisorId(e.target.value)}
                  className="sidebar-form-input"
                  title={!selectedUser ? 'Se asigna tras crear el usuario' : undefined}
                >
                  <option value="">
                    {defaultSupervisor
                      ? `${defaultSupervisor.name} (POR DEFECTO)`
                      : 'SIN SUPERVISOR POR DEFECTO — NO PODRÁ APROBAR COMPRAS'}
                  </option>
                  {supervisorCandidates
                    .filter(u => u.status === 'active' && u.id !== selectedUser?.id && !u.isDefaultSupervisor)
                    .map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                </select>
              </div>
            </div>

            <div className="sidebar-form-group">
              <label className={`users-view-checkbox-label ${!selectedUser || initialFormStateRef.current.isDefaultSupervisor ? 'disabled' : ''}`}
                title={
                  !selectedUser
                    ? 'Se marca tras crear el usuario'
                    : initialFormStateRef.current.isDefaultSupervisor
                      ? 'Para cambiarlo, marca a otro usuario como supervisor por defecto'
                      : defaultSupervisor
                        ? `Sustituirá a ${defaultSupervisor.name} como supervisor por defecto`
                        : undefined
                }
              >
                <input
                  type="checkbox"
                  checked={formIsDefaultSupervisor}
                  disabled={!selectedUser || initialFormStateRef.current.isDefaultSupervisor}
                  onChange={e => setFormIsDefaultSupervisor(e.target.checked)}
                />
                Supervisor por defecto (aprueba las compras de quien no tenga supervisor)
              </label>
            </div>
          </SidebarForm>
        )}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteModalUser}
        type="urgent"
        title="Eliminar usuario"
        message={`¿Estás seguro de que deseas eliminar al usuario "${deleteModalUser?.name || deleteModalUser?.firstName || deleteModalUser?.email}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDeleteUser}
        onCancel={() => setDeleteModalUser(null)}
      />

      <Modal
        isOpen={showDiscardModal}
        type="warning"
        title="Descartar cambios"
        message="¿Desea descartar los cambios no guardados y salir del formulario, o prefiere continuar editando?"
        confirmText="Descartar y salir"
        cancelText="Continuar editando"
        onConfirm={() => {
          setShowDiscardModal(false);
          setSelectedUser(null);
          setIsFormOpen(false);
        }}
        onCancel={() => setShowDiscardModal(false)}
      />
    </>
  );
};
